import React, { useRef, useState, useEffect, useCallback } from "react";
import KRGlue from "@lyracom/embedded-form-glue";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../../../../../core/common-service/CommonService";
import {
  getAudiobookPaymentStatusCallback,
  PAYMENT_GATEWAY_PUBLIC_KEY,
  postCreateAudiobookPaymentForm,
  postGenerateAudioBook,
} from "../../../../../../../core/api/Api";
import { trackCheckoutEvent } from "../../../../../../../core/utils/trackCheckoutEvent";
import useLangNavigate from "../../../../../../common/useLangNavigate";
import { getCurrencyForLocale, getLocaleFromPath, DEFAULT_LOCALE } from "../../../../../../../config/locales";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../../../../../../core/constants/constants";

const AudiobookPayment = () => {
  const { t, i18n } = useTranslation();
  const activeLocale = getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
  const currency = getCurrencyForLocale(activeLocale);
  const [message, setMessage] = useState("");
  const navigate = useLangNavigate();
  const location = useLocation();

  const { service_session_id, standalone_text, price: rawPrice, voice_type, coupon_code } =
    location.state || {};
  const price = rawPrice != null ? parseFloat(rawPrice) : undefined;
  const [isPaymentFormRendered, setIsPaymentFormRendered] = useState(false);
  const initiationAmountRef = useRef(null);

  // Determine if this is standalone or book-based audiobook
  const isStandalone = !!standalone_text;
  const identifier = isStandalone ? "standalone" : service_session_id;

  const loadPaymentForm = useCallback(
    async (formToken, orderId) => {
      const endpoint = PAYMENT_GATEWAY_ENDPOINT;
      const publicKey = PAYMENT_GATEWAY_PUBLIC_KEY;

      const { KR } = await KRGlue.loadLibrary(endpoint, publicKey);

      await KR.setFormConfig({
        formToken,
        "kr-language": i18n.language,
      });

      await KR.renderElements("#myPaymentForm");
      setIsPaymentFormRendered(true);

      await KR.onSubmit(async (res) => {
        const orderStatus = res.clientAnswer.orderStatus;
        if (orderStatus === "PAID") {
          getAudiobookPaymentStatusCallback(orderId)
            .then((res) => {
              const response = res?.data?.success;
              if (response) {
                // FIXED: Pass correct payload based on type
                const payload = isStandalone
                  ? {
                      standalone_text,
                      voice_type,
                    }
                  : {
                      service_session_id,
                      voice_type,
                    };

                postGenerateAudioBook(payload)
                  .then((res) => {
                    if (res?.data?.success) {
                      showToast({
                        type: "success",
                        message:
                          res?.data?.message ||
                          t("audiobook.generation_started"),
                      });

                      // Track successful purchase
                      trackCheckoutEvent({
                        eventType: "audiobook_purchase",
                        transactionId: orderId,
                        value: initiationAmountRef.current ?? res?.data?.price ?? 0,
                        items: [
                          {
                            item_id: identifier,
                            item_name: isStandalone
                              ? "Standalone Audiobook Generation"
                              : "Audiobook Generation",
                            item_category: "Audiobook",
                            price: initiationAmountRef.current ?? res?.data?.price ?? 0,
                            quantity: 1,
                          },
                        ],
                        output: "success",
                      });

                      navigate(`/thankyou/purchase?order=${identifier}`);
                    } else {
                      showToast({
                        type: "error",
                        message: t("audiobook_generation_failed"),
                      });
                    }
                  })
                  .catch((error) => {
                    // FIXED: Better error handling
                    const errorMessage =
                      error?.response?.data?.error ||
                      t("generate_audiobook_call_failed");
                    showToast({
                      type: "error",
                      message: errorMessage,
                    });
                  });
              }
            })
            .catch(() => {
              trackCheckoutEvent({
                eventType: "audiobook_purchase",
                transactionId: orderId,
                value: initiationAmountRef.current ?? 0,
                items: [
                  {
                    item_id: identifier,
                    item_name: isStandalone
                      ? "Standalone Audiobook Generation"
                      : "Audiobook Generation",
                    item_category: "Audiobook",
                    price: initiationAmountRef.current ?? 0,
                    quantity: 1,
                  },
                ],
                output: "fail",
              });
            });
        }
        return false;
      });

      await KR.onError(async (res) => {
        const detailedErrorMessage = res.detailedErrorMessage;
        try {
          await getAudiobookPaymentStatusCallback(orderId);
          trackCheckoutEvent({
            eventType: "audiobook_purchase",
            transactionId: orderId,
            value: initiationAmountRef.current ?? 0,
            items: [
              {
                item_id: identifier,
                item_name: isStandalone
                  ? "Standalone Audiobook Generation"
                  : "Audiobook Generation",
                item_category: "Audiobook",
                price: initiationAmountRef.current ?? 0,
                quantity: 1,
              },
            ],
            output: "fail",
          });
        } catch (error) {
          trackCheckoutEvent({
            eventType: "audiobook_purchase",
            transactionId: orderId,
            value: initiationAmountRef.current ?? 0,
            items: [
              {
                item_id: identifier,
                item_name: isStandalone
                  ? "Standalone Audiobook Generation"
                  : "Audiobook Generation",
                item_category: "Audiobook",
                price: initiationAmountRef.current ?? 0,
                quantity: 1,
              },
            ],
            output: "fail",
          });
        }
        showToast({ type: "error", message: t("payment_form_error") });
      });
    },
    [
      i18n.language,
      navigate,
      identifier,
      isStandalone,
      standalone_text,
      voice_type,
      price,
      t,
      service_session_id,
      coupon_code,
    ]
  );

  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    window.scroll(0, 0);

    async function setupPaymentForm() {
      try {
        const payload = isStandalone
          ? {
              standalone_text,
              price,
              voice_type,
              coupon_code: coupon_code || null,
            }
          : {
              service_session_id,
              price,
              voice_type,
              coupon_code: coupon_code || null,
            };

        postCreateAudiobookPaymentForm(payload)
          .then(async (res) => {
            const response = res?.data?.success;
            if (response) {
              const formToken = response?.formToken;
              const orderId = response?.order_id;
              initiationAmountRef.current = response?.final_amount ?? price ?? null;
              loadPaymentForm(formToken, orderId);
            }
          })
          .catch(() => {
            navigate(-1);
            setMessage(t("payment_form_setup_failed"));
          });
      } catch (error) {
        navigate(-1);
        setMessage(t("payment_setup_unknown_error"));
      }
    }
    setupPaymentForm();
  }, [
    loadPaymentForm,
    navigate,
    service_session_id,
    standalone_text,
    isStandalone,
    voice_type,
    price,
    t,
  ]);

  return (
    <section className="aai-features py-100 payment">
      <div className="form">
        <div className="d-flex align-items-center justify-content-center">
          <div id="myPaymentForm">
            <div className="kr-smart-form" kr-card-form-expanded="true"></div>
          </div>
          <div data-test="payment-message">{message}</div>
        </div>
        {isPaymentFormRendered && (
          <div className="centered-div">{t("for_more_info_contact_us")}</div>
        )}
      </div>
    </section>
  );
};

export default AudiobookPayment;
